import { useState } from 'react';
import { Loader2, ExternalLink, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Launches a Stripe-hosted Customer Portal session and redirects the
 * user to it. The portal lets the customer update their card, view
 * invoices, and cancel — without us building any of those screens.
 *
 * The session URL is one-time and short-lived; we always re-fetch on
 * click rather than caching.
 */
export default function CustomerPortalButton({
  testId = 'open-customer-portal-btn',
  className = '',
  variant = 'outline',
  label = 'Manage Billing',
}) {
  const { authToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const open = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/billing/portal/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ return_url: window.location.href }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || `HTTP ${res.status}`);
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Button
        variant={variant}
        onClick={open}
        disabled={isLoading}
        className={className || 'border-teal-200 text-teal-700 hover:bg-teal-50'}
        data-testid={testId}
      >
        {isLoading ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening...</>
        ) : (
          <><ExternalLink className="w-4 h-4 mr-2" /> {label}</>
        )}
      </Button>
      {error && (
        <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 max-w-md">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
